import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createMultiplePosts,
    fetchProject,
    getRecentProjects,
} from '../../shared/api/textProjectApi';
import { useAppDispatch } from '../../store/store';
import s from './ThreeSimilarPostsPage.module.css';
import LogoComponent from '../../shared/ui/logo/LogoComponent';
import Button from '../../shared/ui/button/Button';
import { useSelector } from 'react-redux';

import {
    selectCurrentProject,
    selectScriptData,
    selectMultiplePosts,
    selectPostsToPublish,
} from '../../store/selectors/projectSelectors';
import { setImageProject, setSelectedImage } from '../../store/slices/imageSlice';
import { PostModal } from '../../shared/ui/post-modal/PostModal';

import UserProfileButton from '../../shared/ui/user-profile-button/UserProfileButton';
import { setPricingModal, toggleActivePrompt } from '../../store/slices/pageSlice';

import {
    setCurrentProject,
    setImageData,
    setPostsToPublish,
    setScriptData,
    setSelectedSocialMedia,
    setVideoData,
} from '../../store/slices/projectSlice';
import { setProjectNotes } from '../../store/slices/notesSlice';

import AvatarComponent from '../../shared/ui/avatar/AvatarComponent';
import { selectUser } from '../../store/selectors/authSelectors';
import LoadingGeneration from '../../shared/ui/loading-generation/LoadingGeneration';
import IconStars from '../../shared/ui/icons/IconStars';
import IconLock from '../../shared/ui/icons/IconLock';
const ThreeSimilarPostsPage = () => {
    const { projectId } = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [showPostModal, setShowPostModal] = useState(false);
    const currentProject = useSelector(selectCurrentProject);
    const content_type = currentProject?.content_type;

    const projects = useSelector(selectMultiplePosts);

    const user = useSelector(selectUser);
    const postsToPublish = useSelector(selectPostsToPublish);
    const [isLoading, setIsLoading] = useState(false);
    const [restrictGenerate, setRestrictGenerate] = useState(true);

    const generateMultiplePosts = async (content_type: string | undefined) => {
        try {
            setIsLoading(true);
            if (projectId && content_type) {
                await dispatch(
                    createMultiplePosts({
                        projectId: projectId,
                        platform: content_type || 'threads',
                    }),
                ).unwrap();
                setRestrictGenerate(true);
            } else {
                console.log('Missing projectId or content_type:', { projectId, content_type });
            }
        } catch (error: any) {
            console.error('Failed to create multiple posts:', error);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (projectId) {
            const fetchData = async () => {
                const project = await dispatch(fetchProject({ projectId: projectId })).unwrap();
                generateMultiplePosts(project.project.content_type);
            };
            fetchData();
            dispatch(toggleActivePrompt('create_post'));
        } else {
            console.log('projectId is falsy:', projectId);
        }
    }, [dispatch, projectId, content_type]);

    useEffect(() => {
        return () => {
            dispatch(toggleActivePrompt(null));
            dispatch(setSelectedImage({ image: null, layoutId: null }));
            dispatch(setCurrentProject(null));
            dispatch(setImageData(null));
            dispatch(setScriptData(null));
            dispatch(setProjectNotes([]));
            dispatch(setImageProject(null));
            dispatch(toggleActivePrompt(null));
            dispatch(setSelectedSocialMedia(null));
            dispatch(setVideoData(null));
        };
    }, []);

    const backNavigation = () => {
        navigate(-1);
    };

    return projects && projects.length > 0 && !isLoading ? (
        <>
            <div className={s.container}>
                <div className={s.top_container}>
                    <LogoComponent />
                    <div className={s.top_right_container}>
                        <UserProfileButton />
                    </div>
                </div>
                <div className={s.center_container}>
                    <div className={s.left_center_container}>
                        <h3 className={s.title}>Your visuals are ready!</h3>
                        <p className={s.subtitle}>
                            Take a look at the newly generated images and select your favorite.
                        </p>

                        {!restrictGenerate && (
                            <Button
                                text="Regenerate again"
                                onClick={() => generateMultiplePosts(content_type)}
                                icon={<IconStars color="#fff" width={16} height={16} />}
                                iconPosition="left"
                                style={{
                                    background: 'linear-gradient(90deg, #8801FF 0%, #D400FF 100%)',
                                    color: '#fff',
                                }}
                            />
                        )}
                        {restrictGenerate && (
                            <Button
                                text="Unlock Regeneration"
                                onClick={() => dispatch(setPricingModal(true))}
                                icon={<IconLock width={16} height={16} />}
                                iconPosition="left"
                                style={{ background: '#FFDD35', color: '#000' }}
                            />
                        )}
                        {!restrictGenerate && (
                            <p className={s.subtitle}>You have 1 free post regeneration on us!</p>
                        )}
                        {restrictGenerate && (
                            <p className={s.subtitle}>
                                Unlock more regenerations with our{' '}
                                <span className={s.highlight}>Premium Subscription</span>
                            </p>
                        )}
                    </div>
                    <div className={s.right_center_container}>
                        {projects.map((project) => {
                            const isSelected = postsToPublish.some((p: any) => p.id === project.id);

                            return (
                                <div
                                    key={project.id}
                                    className={`${s.project_container}`}
                                    style={{
                                        cursor: 'pointer',
                                        position: 'relative',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {
                                            if (isSelected) {
                                                dispatch(
                                                    setPostsToPublish(
                                                        postsToPublish.filter(
                                                            (p: any) => p.id !== project.id,
                                                        ),
                                                    ),
                                                );
                                            } else {
                                                dispatch(
                                                    setPostsToPublish([...postsToPublish, project]),
                                                );
                                            }
                                        }}
                                        className={s.project_checkbox}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            left: '10px',
                                            zIndex: 10,
                                            transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    />
                                    <div
                                        className={s.project}
                                        onClick={() => {
                                            if (isSelected) {
                                                dispatch(
                                                    setPostsToPublish(
                                                        postsToPublish.filter(
                                                            (p: any) => p.id !== project.id,
                                                        ),
                                                    ),
                                                );
                                            } else {
                                                dispatch(
                                                    setPostsToPublish([...postsToPublish, project]),
                                                );
                                            }
                                        }}
                                        style={{
                                            opacity: isSelected ? 0.5 : 1,
                                            transition: 'opacity 0.2s ease',
                                        }}
                                    >
                                        {project.image?.url ? (
                                            <img
                                                src={project.image.url}
                                                alt={project.title || 'Project'}
                                                className={s.project_image}
                                            />
                                        ) : (
                                            <div className={s.empty_image} />
                                        )}

                                        <div className={s.user_info}>
                                            <AvatarComponent
                                                avatarUrl={user?.profile_picture ?? ''}
                                                userName={
                                                    user?.name ||
                                                    user?.username ||
                                                    user?.email ||
                                                    ''
                                                }
                                                width={20}
                                                height={20}
                                            />
                                            <p className={s.user_name}>
                                                {user?.name || user?.username || user?.email || ''}
                                            </p>
                                        </div>
                                        <p className={s.project_name}>{project.title}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className={s.bottom_container}>
                    <Button
                        text="Back"
                        onClick={backNavigation}
                        style={{
                            width: '100px',
                            fontWeight: '600',
                        }}
                    />

                    <Button
                        text="Post now!"
                        onClick={() => dispatch(setPricingModal(true))}
                        icon={<IconLock width={16} height={16} />}
                        iconPosition="left"
                        state={postsToPublish.length === 0 ? 'disabled' : 'enabled'}
                        style={{
                            width: '130px',
                            fontWeight: '600',
                            backgroundColor: postsToPublish.length === 0 ? '#ccc' : '#FFDD35',
                            color: postsToPublish.length === 0 ? '#666' : '#000',
                        }}
                    />
                </div>
            </div>

            <PostModal isOpen={showPostModal} onClose={() => setShowPostModal(false)} />
        </>
    ) : (
        <LoadingGeneration />
    );
};

export default ThreeSimilarPostsPage;
